import React, { useState, useEffect } from 'react';
import { HiOutlinePlay, HiOutlinePhotograph, HiOutlineArrowRight, HiOutlineX } from 'react-icons/hi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './MediaPreview.css';
import { mediaService } from '../services/mediaService';
import { flyersService } from '../services/flyersService';
import { supabase } from '../supabaseClient';
import { normalizeStorageUrl } from '../utils/storageUrl';
import GSIDAPDF from '../images/GSIDAPDF.jpg';



function MediaPreview({ onNavigate, edition: customEdition }) {
  const [mediaItems, setMediaItems] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [mainVideoIndex, setMainVideoIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadPreviewData();
  }, [customEdition]);

  // Cycle through images automatically every 3 seconds
  useEffect(() => {
    // Separate images and videos for this effect
    const images = mediaItems.filter(item => item.type === 'image');
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [mediaItems]);

  const loadPreviewData = async () => {
    try {
      setLoading(true);
      setCacheBuster(Date.now());
      
      let editionYear = customEdition;
      if (!editionYear) {
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const [videoData, imageData, flyersData] = await Promise.all([
        mediaService.getMediaData(editionYear),
        mediaService.getMediaImages(editionYear),
        flyersService.getFlyersData(editionYear)
      ]);
      const videos = (videoData || []).map(v => ({ ...v, type: 'video' }));
      const images = (imageData || []).map(i => ({ ...i, type: 'image' }));
      setMediaItems([...videos, ...images]);
      setFlyers((flyersData?.flyers || []).filter(f => f.showOnMedia));
    } catch (error) {
      console.error('Error loading media preview:', error);
      setMediaItems([]);
      setFlyers([]);
    } finally {
      setLoading(false);
    }
  };

  const getCacheBustedUrl = (url) => {
    if (!url) return '';
    try {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}cb=${cacheBuster}`;
    } catch (e) {
      return url;
    }
  };

  const handleMediaClick = (item, idx, isVideo) => {
    if (isVideo) {
      setMainVideoIndex(idx);
    } else {
      setSelectedMedia(item);
    }
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  const handleFlyerClick = (flyer) => {
    if (flyer.downloadUrl) {
      window.open(flyer.downloadUrl, '_blank');
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    try {
      // YouTube (hide controls, branding, set HD)
      const ytParams = 'autoplay=1&rel=0&controls=0&modestbranding=1&showinfo=0&iv_load_policy=3&fs=1&vq=hd1080';
      if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const videoId = urlParams.get('v');
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?${ytParams}`;
        }
      }
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0]?.split('/')[0];
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?${ytParams}`;
        }
      }
      if (url.includes('youtube.com/embed/')) {
        // Remove existing params and add ours
        const base = url.split('?')[0];
        return `${base}?${ytParams}`;
      }
      // Vimeo (no controls)
      if (url.includes('vimeo.com/')) {
        const videoId = url.split('vimeo.com/')[1]?.split('?')[0]?.split('/')[0];
        if (videoId) {
          return `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0&controls=0`;
        }
      }
      // Direct video file
      if (url.match(/\.(mp4|webm|ogg)$/i)) {
        return url;
      }
      return url;
    } catch (error) {
      console.error('Error parsing video URL:', error);
      return url;
    }
  };

  // Separate images and videos
  const images = mediaItems.filter(item => item.type === 'image');
  const videos = mediaItems.filter(item => item.type === 'video');
  const mainVideo = videos[mainVideoIndex] || videos[0];
  const currentImageUrl = normalizeStorageUrl(images[currentImageIndex]?.thumbnail || images[currentImageIndex]?.imageUrl);

  // Responsive styles
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 800;
  const sectionStyle = isMobile
    ? { width: '100%', maxWidth: 600, margin: '0 auto', boxSizing: 'border-box', padding: 0, position: 'relative' }
    : { width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', boxSizing: 'border-box' };
  const containerStyle = isMobile
    ? { width: '100%', maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16, flexWrap: 'wrap', boxSizing: 'border-box', alignItems: 'center', justifyContent: 'center', padding: 0 }
    : { width: '100vw', display: 'flex', flexDirection: 'row', gap: '32px', flexWrap: 'wrap', boxSizing: 'border-box' };
  const imageColStyle = isMobile
    ? { width: '90vw', maxWidth: 400, minWidth: 0, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', height: 'auto', alignSelf: 'center', alignItems: 'center' }
    : { flex: 1.2, minWidth: 200, maxWidth: 350, display: 'flex', flexDirection: 'column', gap: '20px', justifyContent: 'center', height: '100%' };
  const imageThumbStyle = isMobile
    ? { cursor: 'pointer', overflow: 'hidden', aspectRatio: '4/5', background: 'transparent', width: '100%', height: '60vw', maxHeight: 400, minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }
    : { cursor: 'pointer', overflow: 'hidden', aspectRatio: '4/5', background: 'transparent', width: '100%', height: 'calc(100vh * 9 / 16)', maxHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' };
  const imageStyle = isMobile
    ? { width: '100%', height: '100%', objectFit: 'contain', aspectRatio: '4/5', display: 'block', background: 'transparent' }
    : { width: '100%', height: '100%', objectFit: 'contain', aspectRatio: '4/5', display: 'block', background: 'transparent' };
  const videoColStyle = isMobile
    ? { width: '100vw', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', flexWrap: 'wrap' }
    : { flex: 3, minWidth: 350, display: 'flex', flexDirection: 'row', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' };
  const mainVideoStyle = isMobile
    ? { width: '100vw', maxWidth: 600, aspectRatio: '16/9', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', minWidth: 0, minHeight: 180, margin: '0 auto', alignSelf: 'center', display: 'flex', justifyContent: 'center' }
    : { width: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', flex: 6, minWidth: 600, minHeight: 420 };
  const videoThumbsStyle = isMobile
    ? { display: 'flex', flexDirection: 'row', gap: 0, marginLeft: 0, flex: 1, minWidth: 0, maxWidth: '100vw', width: '100vw', overflowX: 'auto' }
    : { display: 'flex', flexDirection: 'column', gap: '18px', marginLeft: 18, flex: 1, minWidth: 220, maxWidth: 300 };

  return (
    <div className="media-preview-section" style={sectionStyle}>
      <div className="media-preview-container" style={containerStyle}>
        {loading ? (
          <div className="loading-spinner"></div>
        ) : (mediaItems.length === 0 ? (
          <p>Aucun média disponible</p>
        ) : (
          <>
            {/* Only one image at a time, auto-cycling */}
            <div className="media-preview-images" style={imageColStyle}>
              {images.length > 0 && (
                <div className="media-image-thumb" style={imageThumbStyle} onClick={() => handleMediaClick(images[currentImageIndex], currentImageIndex, false)}>
                  <img
                    src={getCacheBustedUrl(currentImageUrl)}
                    alt={images[currentImageIndex]?.title}
                    loading="lazy"
                    style={imageStyle}
                  />
                </div>
              )}
            </div>

            {/* Videos on the right, thumbnails to the right of main video, border radius, responsive */}
            <div className="media-preview-videos" style={videoColStyle}>
              {/* Main video (grande) */}
              {mainVideo && (
                <div className="main-video" style={mainVideoStyle}>
                  {mainVideo.videoUrl?.match(/\.(mp4|webm|ogg)$/i) ? (
                    <video controls autoPlay style={{ width: '100%', height: '100%' }}>
                      <source src={mainVideo.videoUrl} type="video/mp4" />
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  ) : (
                    <iframe
                      src={getEmbedUrl(mainVideo.videoUrl)}
                      title={mainVideo.title}
                      style={{ width: '100%', height: '100%', border: 0,  }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  )}
                  {/* <div className="main-video-title" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.4)', color: '#fff', padding: '8px 16px', fontWeight: 500 }}>{mainVideo.title}</div> */}
                </div>
              )}
              {/* Video thumbnails (autres videos) to the right of main video */}
              <div className="video-thumbnails" style={videoThumbsStyle}>
                {isMobile && videos.length > 3 ? (
                  <Swiper
                    spaceBetween={0}
                    slidesPerView={2.2}
                    style={{ width: '100%', padding: '0' }}
                  >
                    {videos.map((item, idx) => (
                      idx !== mainVideoIndex && (
                        <SwiperSlide key={idx} style={{ width: 'auto' }}>
                          <div className="video-thumb" style={{ width: 140, aspectRatio: '16/9', overflow: 'hidden', position: 'relative', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', background: '#f7f7f7', minHeight: 80, maxHeight: 120 }} onClick={() => handleMediaClick(item, idx, true)}>
                            <img
                              src={getCacheBustedUrl(item.thumbnail || item.imageUrl)}
                              alt={item.title}
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', minHeight: 80, maxHeight: 120 }}
                            />
                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                              <button
                                className="play-overlay"
                                aria-label="Play video"
                                style={{ pointerEvents: 'auto', background: 'rgba(0,0,0,0.3)', border: 'none', borderRadius: '50%', color: '#fff', fontSize: 24, padding: 4 }}
                              >
                                <HiOutlinePlay />
                              </button>
                            </div>
                          </div>
                        </SwiperSlide>
                      )
                    ))}
                  </Swiper>
                ) : (
                  videos.map((item, idx) => (
                    idx !== mainVideoIndex && (
                      <div key={idx} className="video-thumb" style={{ width: '100%', aspectRatio: '16/9',  overflow: 'hidden', position: 'relative', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', background: '#f7f7f7', minHeight: 80, maxHeight: 120 }} onClick={() => handleMediaClick(item, idx, true)}>
                        <img
                          src={getCacheBustedUrl(item.thumbnail || item.imageUrl)}
                          alt={item.title}
                          loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover',  display: 'block', minHeight: 80, maxHeight: 120 }}
                        />
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                          <button
                            className="play-overlay"
                            aria-label="Play video"
                            style={{ pointerEvents: 'auto', background: 'rgba(0,0,0,0.3)', border: 'none', borderRadius: '50%', color: '#fff', fontSize: 24, padding: 4 }}
                          >
                            <HiOutlinePlay />
                          </button>
                        </div>
                      </div>
                    )
                  ))
                )}
              </div>
            </div>
          </>
        ))}

        {/* Flyers horizontal shortcut */}
        {!loading && flyers.length > 0 && (
          <div className="media-preview-flyers">
            <div className="flyers-shortcut-header">
              <h3>INFORMATIONS CLÉS</h3>
            </div>
            <div className="flyers-horizontal-list">
              {flyers.map((flyer) => (
                <div 
                  key={flyer.id} 
                  className="media-flyer-item"
                  onClick={() => handleFlyerClick(flyer)}
                >
                  <div className="media-flyer-book">
                    <img 
                      src={flyer.image} 
                      alt={flyer.title}
                      onError={(e) => {
                        e.target.src = GSIDAPDF;
                      }}
                    />
                    <div className="media-flyer-shadow"></div>
                  </div>
                  <span className="media-flyer-title">{flyer.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="media-preview-footer" style={isMobile ? { marginTop: 24, width: '100%', display: 'flex', justifyContent: 'center' } : { marginTop: 24 }}>
        <button 
          className="voir-plus-button"
          onClick={() => onNavigate('media')}
        >
          Voir plus
          <HiOutlineArrowRight />
        </button>
      </div>

      {selectedMedia && (
        <div className="video-modal" onClick={closeModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeModal}>
              <HiOutlineX />
            </button>
            {selectedMedia.type === 'video' ? (
              selectedMedia.videoUrl?.match(/\.(mp4|webm|ogg)$/i) ? (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                  <video controls autoPlay style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    <source src={selectedMedia.videoUrl} type="video/mp4" />
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                </div>
              ) : (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                  <iframe
                    src={getEmbedUrl(selectedMedia.videoUrl)}
                    title={selectedMedia.title}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              )
            ) : (
              <div className="image-modal-content">
                <img src={getCacheBustedUrl(selectedMedia.imageUrl)} alt={selectedMedia.title} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaPreview;
